/**
 * FibVwap Smart v2 — Optimized Fib+VWAP strategy with data-driven filters
 *
 * Based on analysis of NIFTY trades:
 * - Only 23.6% and 38.2% fib levels (65%+ WR vs 33% for others)
 * - Skip first hour (before 10:00 IST, consistently loses)
 * - Favor SHORT (67% WR vs 41% LONG)
 * - Early exit if no 0.3R favorable movement within 2 scan cycles
 *
 * Uses same backend /fib-vwap endpoint, filters signals client-side.
 * Trade strategy tag: "fib-vwap-smart2" (separate from "fib-vwap")
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { ERA_VIEWS } from "./eras";
import { isExpiryDay, tradingDaysOf } from "./expiry";
import { alertTradePlaced, alertTradeClosed } from "./alerts";
import TradeDrawer from "./TradeDrawer";
import TradeCards from "./TradeCards";
import { peakConcurrentCapital } from "./capitalUsage";

const API = "/api/zerodha";
const STRATEGY = "fib-vwap-smart2";

// Optimized fib levels (data shows 23.6% and 38.2% have 65%+ WR)
const ALLOWED_FIBS = new Set(["23.6%", "38.2%"]);

const FIB_TARGET_MAP = {
  "23.6%": 2.5,
  "38.2%": 2.5,
  "50%": 2.0,
  "61.8%": 1.5,
  "78.6%": 1.5,
};

function exportLiveTradesCSV(trades, filename) {
  if (!trades.length) return;
  const headers = "Time,Index,Type,Fib,Pattern,Option,Qty,Entry,SL,Target,Buy Prem,Sell Prem,Capital,P&L,Status\n";
  const rows = trades.map(t =>
    `${t.time},${t.index||""},${t.type},${t.fibLevel||""},${t.signalType} ${t.strike},${t.qty},${t.entry},${t.sl},${t.target},${t.entryPremium||""},${t.exitPremium||""},${t.capital||""},${t.netPnl||t.grossPnl||""},${t.status}${t.exitReason ? " ("+t.exitReason+")" : ""}`
  ).join("\n");
  const blob = new Blob([headers + rows], { type: "text/csv" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${filename}.csv`; a.click();
}

function pickDeepItmStrike(optionChain, isPut) {
  const step = optionChain.strikeStep || 50;
  const spot = optionChain.spotPrice ?? optionChain.atmStrike;
  const base = Math.floor(spot / step) * step;
  return isPut ? base + 2 * step : base - step;
}

const lotsForIndex = (ix) => ix === "SENSEX" ? "10" : "5";

export default function FibVwapSmart2({ broker = "zerodha", index = "NIFTY" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [timeframe, setTimeframe] = useState("15minute");
  const [days, setDays] = useState("1");
  const [scanTarget, setScanTarget] = useState("2");
  const [scanLots, setScanLots] = useState(lotsForIndex(index));
  const [scanStrike, setScanStrike] = useState("ATM");
  const [useVwap, setUseVwap] = useState(true);
  const [useSR, setUseSR] = useState(true);
  const [tradeDirection, setTradeDirection] = useState("BOTH");

  // Smart filters (the optimization)
  const [useSmartFibs, setUseSmartFibs] = useState(true);       // only 23.6% & 38.2%
  const [skipFirstHour, setSkipFirstHour] = useState(true);      // skip before 10:00 IST
  const [earlyExitEnabled, setEarlyExitEnabled] = useState(true); // exit if no 0.3R move in 5 cycles

  // Trade guards
  const [maxTradesPerDay, setMaxTradesPerDay] = useState("10");
  const [skipFirstMins, setSkipFirstMins] = useState("45"); // 45 mins = skip first hour from 9:15

  // Auto-scan
  const [scanning, setScanning] = useState(false);
  const [scanInterval, setScanInterval] = useState("10");
  const [lastScanTime, setLastScanTime] = useState(null);
  const scanIntervalRef = useRef(null);

  // Auto-trade
  const [autoTrading, setAutoTrading] = useState(false);
  const [simulateMode, setSimulateMode] = useState(true);
  const [tradeLog, setTradeLog] = useState([]);
  const [drawerTrade, setDrawerTrade] = useState(null);

  // Load persisted trades
  useEffect(() => {
    axios.get(`/api/monitor/status?strategy=${STRATEGY}&index=${index}`).then(res => {
      if (res.data.success && res.data.trades?.length > 0) {
        setTradeLog(res.data.trades.map(t => ({
          ...t, id: t.id || Date.now() + Math.random(),
          time: t.placedAt || t.time,
          signalType: t.type === "SHORT" ? "PE" : "CE",
        })));
      }
    }).catch(() => {});
  }, [index]);

  const [tradeError, setTradeError] = useState("");
  const placingTradeRef = useRef(false);
  const processedSignalsRef = useRef(new Set());
  const [showStopConfirm, setShowStopConfirm] = useState(false);

  // Live price tracking
  const [liveNiftyPrice, setLiveNiftyPrice] = useState(null);
  const [livePremiums, setLivePremiums] = useState({});
  const tradeLogRef = useRef(tradeLog);
  tradeLogRef.current = tradeLog;
  const monitorRef = useRef(null);

  // Early exit tracking: { tradeId: { scanCount, entryPrice } }
  const earlyExitTracker = useRef({});

  // --- Filtered signals (apply smart filters) ---
  const filteredSignals = useMemo(() => {
    if (!data?.signals) return [];
    return data.signals.filter(sig => {
      // Fib level filter
      if (useSmartFibs && !ALLOWED_FIBS.has(sig.fibLevel)) return false;

      // Time filter: skip before 10:00 IST
      if (skipFirstHour && sig.signalCandle?.date) {
        try {
          const dt = new Date(sig.signalCandle.date);
          const istH = (dt.getUTCHours() + 5 + Math.floor((dt.getUTCMinutes() + 30) / 60)) % 24;
          if (istH < 10) return false;
        } catch {}
      }

      // Direction filter
      if (tradeDirection !== "BOTH" && sig.type !== tradeDirection) return false;

      return true;
    });
  }, [data, useSmartFibs, skipFirstHour, tradeDirection]);

  // --- Scan ---
  const runScan = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await axios.get(`${API}/fib-vwap`, {
        params: { timeframe, days, vwap: useVwap ? "true" : "false", sr: useSR ? "true" : "false", index },
        timeout: 30000,
      });
      if (res.data.success) { setData(res.data.data); setLastScanTime(new Date()); return res.data.data; }
      else setError(res.data.message);
    } catch (err) { setError(err.response?.data?.message || err.message); }
    finally { setLoading(false); }
    return null;
  }, [timeframe, days, useVwap, useSR, index]);

  // --- Place trade ---
  const placeAutoTrade = useCallback(async (signal, optionTrade) => {
    if (!signal || !optionTrade?.suggestions?.length) return;
    const suggestion = optionTrade.suggestions[0];
    const defaultLotSize = index === "BANKNIFTY" ? 30 : index === "SENSEX" ? 20 : 65;
    const lotSize = parseInt(suggestion.lotSize) || defaultLotSize;
    const qty = parseInt(scanLots) * lotSize;
    try {
      let orderId = null;
      if (!simulateMode) {
        const orderExchange = index === "SENSEX" ? "BFO" : "NFO";
        const orderRes = await axios.post(`${API}/place-order`, {
          tradingsymbol: suggestion.symbol, exchange: orderExchange, transaction_type: "BUY",
          quantity: qty, order_type: "MARKET", product: "MIS",
        });
        if (!orderRes.data.success) return null;
        orderId = orderRes.data.data?.orderId;
      } else {
        orderId = "SIM-" + Date.now();
      }

      let actualEntry = signal.entryPrice;
      try {
        const idxRes = await axios.get(`${API}/index-ltp`, { params: { index }, timeout: 5000 });
        if (idxRes.data.success && idxRes.data.ltp) actualEntry = idxRes.data.ltp;
      } catch {}

      const risk = signal.risk;
      const actualSL = signal.type === "SHORT" ? actualEntry + risk : actualEntry - risk;
      const targetMultiple = signal.recommendedTarget || FIB_TARGET_MAP[signal.fibLevel] || parseFloat(scanTarget);
      const actualTarget = signal.type === "SHORT"
        ? actualEntry - risk * targetMultiple
        : actualEntry + risk * targetMultiple;

      let entryPremium = null;
      try {
        const ltpRes = await axios.get(`${API}/option-ltp`, { params: { token: suggestion.token, tradingsymbol: suggestion.symbol }, timeout: 5000 });
        if (ltpRes.data.success) entryPremium = ltpRes.data.ltp;
      } catch {}

      const tradeEntry = {
        id: Date.now(), time: new Date().toISOString(), type: signal.type,
        signalType: signal.type === "SHORT" ? "PE" : "CE",
        tradingsymbol: suggestion.symbol, optionToken: suggestion.token,
        strike: suggestion.strike, strikeType: suggestion.type,
        qty, lots: parseInt(scanLots),
        entry: Math.round(actualEntry * 10) / 10,
        sl: Math.round(actualSL * 10) / 10,
        target: Math.round(actualTarget * 10) / 10,
        signalEntry: signal.entryPrice,
        risk: signal.risk, fibLevel: signal.fibLevel,
        orderId, status: "OPEN", simulated: simulateMode, strategy: STRATEGY, index,
        entryPremium,
        capital: entryPremium ? Math.round(entryPremium * qty) : null,
      };
      setTradeLog(prev => [tradeEntry, ...prev]);
      alertTradePlaced(tradeEntry);
      await axios.post(`${API}/auto-trade/track`, tradeEntry);

      // Track for early exit
      earlyExitTracker.current[tradeEntry.id] = { scanCount: 0, entryPrice: actualEntry };

      return tradeEntry;
    } catch (err) {
      setTradeError(`${simulateMode ? "[SIM] " : ""}Order failed: ${err.response?.data?.message || err.message}`);
    }
  }, [scanLots, simulateMode, scanTarget, index]);

  // --- Exit trade ---
  const exitTrade = useCallback(async (trade, reason = "MANUAL") => {
    try {
      if (!trade.simulated && trade.tradingsymbol) {
        await axios.post(`${API}/exit-order`, {
          tradingsymbol: trade.tradingsymbol, transaction_type: "BUY", quantity: trade.remainingQty || trade.qty,
        });
      }
      const exitPremium = livePremiums[trade.tradingsymbol] || trade.entryPremium || 0;
      const ep = trade.entryPremium || 0;
      const grossPnl = ep && exitPremium ? Math.round((exitPremium - ep) * (trade.qty || 130)) : null;
      const closedTrade = {
        ...trade, status: "CLOSED", exitTime: new Date().toISOString(),
        exitReason: reason, exitPremium, grossPnl, netPnl: grossPnl,
      };
      setTradeLog(prev => prev.map(t => t.id === trade.id ? closedTrade : t));
      alertTradeClosed(closedTrade);
      await axios.post(`${API}/auto-trade/update`, {
        id: trade.id, status: "CLOSED", exitReason: reason, exitPremium, grossPnl, netPnl: grossPnl,
      });
      delete earlyExitTracker.current[trade.id];
    } catch (err) {
      setTradeError(`Exit failed: ${err.message}`);
    }
  }, [livePremiums]);

  // --- Toggle scan ---
  const toggleScan = useCallback(() => {
    if (scanning) {
      if (scanIntervalRef.current) { clearInterval(scanIntervalRef.current); scanIntervalRef.current = null; }
      setScanning(false);
    } else {
      setScanning(true); runScan();
      scanIntervalRef.current = setInterval(() => { runScan(); }, parseInt(scanInterval) * 1000);
    }
  }, [scanning, runScan, scanInterval]);

  useEffect(() => {
    return () => {
      if (scanIntervalRef.current) clearInterval(scanIntervalRef.current);
      if (monitorRef.current) clearInterval(monitorRef.current);
    };
  }, []);

  // --- Auto-start scan at 9:15 IST when tab is mounted during market hours ---
  // Smart 2.0 has no server-side strategy code, so the tab must be open for
  // this to fire. Guarded by a ref so we only auto-start once per session.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (autoStartedRef.current || scanning) return;
    const istParts = new Date().toLocaleString("en-US", {
      timeZone: "Asia/Kolkata",
      hour12: false,
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
    // istParts e.g. "Mon, 09:16" — parse weekday + minutes
    const m = istParts.match(/^(\w+),?\s+(\d{2}):(\d{2})/);
    if (!m) return;
    const weekday = m[1];
    const totalMin = parseInt(m[2], 10) * 60 + parseInt(m[3], 10);
    const isWeekday = !["Sat", "Sun"].includes(weekday);
    const inWindow = totalMin >= 9 * 60 + 15 && totalMin <= 15 * 60 + 30;
    if (isWeekday && inWindow) {
      autoStartedRef.current = true;
      toggleScan();
    }
  }, [scanning, toggleScan]);

  // --- Monitor polling ---
  useEffect(() => {
    const poll = async () => {
      try {
        const res = await axios.get(`/api/monitor/status?strategy=${STRATEGY}&index=${index}`, { timeout: 5000 });
        if (res.data.success) {
          const brokerKey = broker || "zerodha";
          if (res.data.lastLTP?.[brokerKey]) setLiveNiftyPrice(res.data.lastLTP[brokerKey]);
          if (res.data.lastLTP?.[`${brokerKey}_${index}`]) setLiveNiftyPrice(res.data.lastLTP[`${brokerKey}_${index}`]);
          const bt = res.data.trades || [];
          if (bt.length > 0) {
            setTradeLog(prev => {
              const map = new Map(prev.map(t => [String(t.id), t]));
              for (const t of bt) {
                const key = String(t.id);
                map.set(key, { ...map.get(key), ...t, time: t.placedAt || t.time, signalType: t.type === "SHORT" ? "PE" : "CE" });
              }
              return Array.from(map.values()).sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0));
            });
          }
        }
      } catch {}

      // Live premiums + early exit check
      for (const t of tradeLogRef.current.filter(t => t.status === "OPEN" && t.tradingsymbol)) {
        try {
          const ltpRes = await axios.get(`${API}/option-ltp`, { params: { token: t.optionToken, tradingsymbol: t.tradingsymbol }, timeout: 3000 });
          if (ltpRes.data.success && ltpRes.data.ltp) {
            const ltp = ltpRes.data.ltp;
            setLivePremiums(prev => ({ ...prev, [t.tradingsymbol]: ltp }));
            if (!t.entryPremium) {
              setTradeLog(prev => prev.map(tr =>
                tr.id === t.id && !tr.entryPremium
                  ? { ...tr, entryPremium: ltp, capital: Math.round(ltp * (tr.qty || 130)) }
                  : tr
              ));
              axios.post(`${API}/auto-trade/update`, { id: t.id, entryPremium: ltp, capital: Math.round(ltp * (t.qty || 130)) }).catch(() => {});
            }
          }
        } catch {}
      }

      // Early exit logic: if trade hasn't moved 0.3R favorable in 2 scan cycles, exit
      if (earlyExitEnabled && liveNiftyPrice) {
        for (const t of tradeLogRef.current.filter(t => t.status === "OPEN")) {
          const tracker = earlyExitTracker.current[t.id];
          if (!tracker) continue;
          tracker.scanCount++;
          const favorable = t.type === "SHORT"
            ? tracker.entryPrice - liveNiftyPrice
            : liveNiftyPrice - tracker.entryPrice;
          const threshold = (t.risk || 50) * 0.3;

          if (tracker.scanCount >= 5 && favorable < threshold) {
            setTradeError(`Early exit: ${t.type} ${t.tradingsymbol} — no ${threshold.toFixed(0)}pt move in ${tracker.scanCount} checks`);
            exitTrade(t, "EARLY EXIT (no momentum)");
          }
        }
      }
    };
    poll();
    monitorRef.current = setInterval(poll, 5000);
    return () => { if (monitorRef.current) { clearInterval(monitorRef.current); monitorRef.current = null; } };
  }, [broker, index, earlyExitEnabled, liveNiftyPrice, exitTrade]);

  // --- Auto-trade signal detection (uses filtered signals) ---
  useEffect(() => {
    if (!autoTrading || !filteredSignals.length || placingTradeRef.current) return;
    const latestSignal = filteredSignals[filteredSignals.length - 1];

    const signalKey = `${latestSignal.type}_${latestSignal.entryPrice}_${latestSignal.triggerCandle?.date}`;
    if (processedSignalsRef.current.has(signalKey)) return;

    const alreadyTraded = tradeLog.some(t => t.entry === latestSignal.entryPrice && t.type === latestSignal.type);
    if (alreadyTraded) return;

    // Max trades per day guard
    const maxPerDay = parseInt(maxTradesPerDay) || 5;
    const today = new Date().toISOString().split("T")[0];
    const todayTrades = tradeLog.filter(t => (t.time || t.placedAt || "").startsWith(today) && t.strategy === STRATEGY);
    if (todayTrades.length >= maxPerDay) {
      processedSignalsRef.current.add(signalKey);
      setTradeError(`Skipped: max ${maxPerDay} trades/day reached (${todayTrades.length} today)`);
      return;
    }

    // Staleness check
    const lastClose = data?.lastCandle?.close;
    if (lastClose) {
      const drift = Math.abs(lastClose - latestSignal.entryPrice);
      if (drift > latestSignal.risk * 0.5) {
        processedSignalsRef.current.add(signalKey);
        setTradeError(`Skipped: stale signal (price drifted ${drift.toFixed(0)} pts)`);
        return;
      }
    }

    // Build option trade from option chain
    let optionTrade = latestSignal.optionTrade;
    if (!optionTrade && data?.optionChain?.strikes?.length > 0) {
      const atmStrike = data.optionChain.atmStrike;
      const isPut = latestSignal.type === "SHORT";
      const want = pickDeepItmStrike(data.optionChain, isPut);
      const strikeData = data.optionChain.strikes.find(s => s.strike === want) || data.optionChain.strikes.find(s => s.strike === atmStrike);
      const opt = isPut ? strikeData?.pe : strikeData?.ce;
      if (opt) {
        optionTrade = {
          direction: isPut ? "BEARISH" : "BULLISH",
          suggestions: [{
            action: isPut ? "BUY PE" : "BUY CE",
            strike: strikeData.strike, symbol: opt.symbol, token: opt.token,
            lotSize: opt.lotSize || 65, type: "ITM+1",
          }],
        };
      }
    }

    if (optionTrade) {
      placingTradeRef.current = true;
      processedSignalsRef.current.add(signalKey);
      placeAutoTrade(latestSignal, optionTrade).finally(() => { placingTradeRef.current = false; });
    }
  }, [filteredSignals, autoTrading, tradeLog, placeAutoTrade, data, maxTradesPerDay]);

  // --- Toggle auto-trade ---
  const toggleAutoTrade = useCallback(async () => {
    if (autoTrading) {
      const openTrades = tradeLog.filter(t => t.status === "OPEN");
      if (openTrades.length > 0) { setShowStopConfirm(true); return; }
      setAutoTrading(false);
    } else {
      setAutoTrading(true);
      if (!scanning) toggleScan();
    }
  }, [autoTrading, tradeLog, scanning, toggleScan]);

  const handleStopSquareOff = useCallback(async () => {
    for (const trade of tradeLog.filter(t => t.status === "OPEN")) await exitTrade(trade);
    setAutoTrading(false); setShowStopConfirm(false);
  }, [tradeLog, exitTrade]);

  // --- Backtest ---
  const [btData, setBtData] = useState(null);
  const [btLoading, setBtLoading] = useState(false);
  const [btError, setBtError] = useState("");
  const [btTimeframe, setBtTimeframe] = useState("15minute");
  const [btDays, setBtDays] = useState("1");
  const [btTarget, setBtTarget] = useState("2");
  const [btTrailing, setBtTrailing] = useState(true);
  const [btLots, setBtLots] = useState(lotsForIndex(index));
  const [btStrike, setBtStrike] = useState("ATM");
  const [btPartial, setBtPartial] = useState(true);
  const [btDateMode, setBtDateMode] = useState("days");
  const [btFromDate, setBtFromDate] = useState("");
  const [btToDate, setBtToDate] = useState("");
  const [activeView, setActiveView] = useState("scan"); // scan | backtest

  const [btDirFilter, setBtDirFilter] = useState("ALL");

  // Reset lots to the index-appropriate default when the index changes (skip initial mount)
  const lotsIdxInit = useRef(true);
  useEffect(() => {
    if (lotsIdxInit.current) { lotsIdxInit.current = false; return; }
    setScanLots(lotsForIndex(index));
    setBtLots(lotsForIndex(index));
  }, [index]);

  function _smartFilter(trades) {
    return (trades || []).filter(t => {
      if (useSmartFibs && !ALLOWED_FIBS.has(t.fibLevel)) return false;
      if (skipFirstHour && t.triggerDate) {
        try {
          const dt = new Date(t.triggerDate);
          const istH = (dt.getUTCHours() + 5 + Math.floor((dt.getUTCMinutes() + 30) / 60)) % 24;
          if (istH < 10) return false;
        } catch {}
      }
      return true;
    });
  }

  function _recomputeSummary(trades, raw) {
    const wins = trades.filter(t => t.result === "WIN");
    const losses = trades.filter(t => t.result === "LOSS");
    const totalPnl = trades.reduce((s, t) => s + (t.pnl || 0), 0);
    const totalOptionPnl = trades.reduce((s, t) => s + (t.option?.moneyPnl || 0), 0);
    const totalCharges = trades.reduce((s, t) => s + (t.option?.charges?.totalCharges || 0), 0);
    const shortTrades = trades.filter(t => t.type === "SHORT");
    const longTrades = trades.filter(t => t.type === "LONG");
    const dirStats = (arr) => {
      if (!arr.length) return { trades: 0, wins: 0, losses: 0, winRate: 0, totalPnl: 0, totalOptionPnl: 0, avgWin: 0, avgLoss: 0, avgR: 0, profitFactor: 0, maxDrawdown: 0 };
      const w = arr.filter(t => t.result === "WIN"), l = arr.filter(t => t.result === "LOSS");
      const wPnl = w.reduce((s,t) => s + (t.pnl||0), 0), lPnl = Math.abs(l.reduce((s,t) => s + (t.pnl||0), 0));
      let dd = 0, peak = 0, cum = 0;
      arr.forEach(t => { cum += (t.pnl||0); if (cum > peak) peak = cum; if (peak - cum > dd) dd = peak - cum; });
      return {
        trades: arr.length, wins: w.length, losses: l.length,
        winRate: Math.round(w.length / arr.length * 100 * 10) / 10,
        totalPnl: Math.round(arr.reduce((s,t) => s + (t.pnl||0), 0) * 10) / 10,
        totalOptionPnl: Math.round(arr.reduce((s,t) => s + (t.option?.moneyPnl||0), 0)),
        avgWin: w.length ? Math.round(wPnl / w.length * 10) / 10 : 0,
        avgLoss: l.length ? Math.round(-Math.abs(l.reduce((s,t) => s + (t.pnl||0), 0)) / l.length * 10) / 10 : 0,
        avgR: Math.round(arr.reduce((s,t) => s + (t.rMultiple||0), 0) / arr.length * 100) / 100,
        profitFactor: lPnl > 0 ? Math.round(wPnl / lPnl * 100) / 100 : wPnl > 0 ? Infinity : 0,
        maxDrawdown: Math.round(dd * 10) / 10,
      };
    };
    let dd = 0, peak = 0, cum = 0;
    trades.forEach(t => { cum += (t.pnl||0); if (cum > peak) peak = cum; if (peak - cum > dd) dd = peak - cum; });
    const wPnl = wins.reduce((s,t) => s + (t.pnl||0), 0);
    const lPnl = Math.abs(losses.reduce((s,t) => s + (t.pnl||0), 0));
    const lotSize = raw?.summary?.lotSize || 65;
    const lots = raw?.summary?.lots || 2;
    const qty = lotSize * lots;
    const capitalPerTrade = trades.length ? Math.round(trades.reduce((s,t) => s + (t.option?.entryPremium||0) * (t.option?.qty||qty), 0) / trades.length) : 0;
    return {
      ...raw?.summary,
      totalTrades: trades.length, wins: wins.length, losses: losses.length,
      winRate: trades.length ? Math.round(wins.length / trades.length * 100 * 10) / 10 : 0,
      totalPnl: Math.round(totalPnl * 10) / 10,
      totalOptionPnl: Math.round(totalOptionPnl),
      totalCharges: Math.round(totalCharges),
      profitFactor: lPnl > 0 ? Math.round(wPnl / lPnl * 100) / 100 : wPnl > 0 ? Infinity : 0,
      maxDrawdown: Math.round(dd * 10) / 10,
      lotSize, lots, qty, capitalPerTrade,
      totalCapitalUsed: capitalPerTrade * trades.length,
      roi: capitalPerTrade > 0 ? Math.round(totalOptionPnl / (capitalPerTrade * trades.length) * 10000) / 100 : 0,
      strikeType: raw?.summary?.strikeType || "ATM",
    };
  }

  async function runBacktest() {
    setBtLoading(true); setBtError(""); setBtData(null);
    try {
      const btParams = {
        timeframe: btTimeframe,
        days: btDateMode === "custom" ? "0" : btDays,
        ...(btDateMode === "custom" && { fromDate: btFromDate, toDate: btToDate }),
        target: btTarget, trailing: btTrailing ? "true" : "false",
        lots: btLots, strike: btStrike,
        vwap: useVwap ? "true" : "false", sr: useSR ? "true" : "false",
        partial: btPartial ? "true" : "false", index,
      };
      const res = await axios.get(`${API}/fib-vwap/backtest`, { params: btParams, timeout: 120000 });
      if (res.data.success) {
        const raw = res.data.data;
        const filtered = _smartFilter(raw.trades);
        const shortTrades = filtered.filter(t => t.type === "SHORT");
        const longTrades = filtered.filter(t => t.type === "LONG");
        setBtData({
          ...raw,
          trades: filtered,
          summary: _recomputeSummary(filtered, raw),
          shortStats: _recomputeSummary(shortTrades, raw),
          longStats: _recomputeSummary(longTrades, raw),
          // Keep unfiltered for comparison
          _unfilteredCount: raw.trades?.length || 0,
          _unfilteredPnl: Math.round((raw.trades || []).reduce((s, t) => s + (t.option?.moneyPnl || 0), 0)),
        });
      } else setBtError(res.data.message);
    } catch (err) { setBtError(err.response?.data?.message || err.message); }
    setBtLoading(false);
  }

  // --- Trade log date filter (default: today) + summary ---
  const [tlFilterDate, setTlFilterDate] = useState(() => new Date().toLocaleDateString("en-CA"));
  const [tlFilterDateTo, setTlFilterDateTo] = useState("");
  const [tlFilterExpiry, setTlFilterExpiry] = useState("ALL"); // ALL | EXPIRY | NONEXP
  // Keep the today-default fresh: the PWA/browser can stay mounted across days,
  // which froze the From-date at mount time. If the user hasn't changed it,
  // roll it to the new day on focus/resume/day-change.
  const tlAutoDate = useRef(new Date().toLocaleDateString("en-CA"));
  useEffect(() => {
    const refresh = () => {
      const today = new Date().toLocaleDateString("en-CA");
      if (today !== tlAutoDate.current) {
        setTlFilterDate(prev => (prev === tlAutoDate.current ? today : prev));
        tlAutoDate.current = today;
      }
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    const iv = setInterval(refresh, 60000);
    return () => { window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", refresh); clearInterval(iv); };
  }, []);

  const filteredTradeLog = useMemo(() => {
    return tradeLog.filter(t => {
      if (tlFilterExpiry === "EXPIRY" && !isExpiryDay(t)) return false;
      if (tlFilterExpiry === "NONEXP" && isExpiryDay(t)) return false;
      if (!tlFilterDate && !tlFilterDateTo) return true;
      const d = new Date(t.time || t.placedAt);
      if (isNaN(d)) return false;
      const day = d.toLocaleDateString("en-CA");
      if (tlFilterDate && day < tlFilterDate) return false;
      if (tlFilterDateTo && day > tlFilterDateTo) return false;
      return true;
    });
  }, [tradeLog, tlFilterDate, tlFilterDateTo, tlFilterExpiry]);
  const tlSummary = useMemo(() => {
    return filteredTradeLog.reduce((acc, t) => {
      if (t.status === "OPEN") acc.open++;
      else {
        acc.closed++;
        const pnl = t.netPnl || t.grossPnl || 0;
        if (pnl > 0) acc.wins++;
        acc.totalPnl += pnl;
      }
      return acc;
    }, { open: 0, closed: 0, wins: 0, totalPnl: 0 });
  }, [filteredTradeLog]);

  // ===== RENDER =====
  return (
    <div className="five-ema">

      {/* Stop Confirm */}
      {showStopConfirm && (
        <div className="confirm-overlay" onClick={() => setShowStopConfirm(false)}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <div className="confirm-icon">!</div>
            <h3>Active Trades Open</h3>
            <p>You have <strong>{tradeLog.filter(t => t.status === "OPEN").length}</strong> open trade(s).</p>
            <div className="confirm-actions">
              <button className="btn btn-danger" onClick={handleStopSquareOff}>Square Off All & Stop</button>
              <button className="btn btn-warning" onClick={() => { setAutoTrading(false); setShowStopConfirm(false); }}>Keep Open & Stop</button>
              <button className="btn btn-secondary" onClick={() => setShowStopConfirm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h2>Fib+VWAP Smart v2</h2>
          <p className="dashboard-subtitle">ADX&gt;20 trend filter, tight confluence (0.3 ATR), wider SL (0.5 ATR), close-based confirmation, fib-level R:R</p>
        </div>
        <div className="dashboard-actions">
          <select className="ema-select" value={timeframe} onChange={e => setTimeframe(e.target.value)} disabled={scanning}>
            <option value="5minute">5 Min</option><option value="15minute">15 Min</option>
            <option value="30minute">30 Min</option><option value="60minute">1 Hour</option>
          </select>
          <select className="ema-select" value={days} onChange={e => setDays(e.target.value)} disabled={scanning}>
            <option value="1">1 Day</option><option value="2">2 Days</option><option value="3">3 Days</option>
            <option value="5">5 Days</option><option value="10">10 Days</option>
          </select>
          <select className="ema-select" value={scanTarget} onChange={e => setScanTarget(e.target.value)} disabled={scanning}>
            <option value="1">1:1</option><option value="1.5">1:1.5</option>
            <option value="2">1:2</option><option value="3">1:3</option>
          </select>
          <select className="ema-select" value={scanLots} onChange={e => setScanLots(e.target.value)} disabled={scanning}>
            <option value="1">1 Lot</option><option value="2">2 Lots</option>
            <option value="3">3 Lots</option><option value="5">5 Lots</option>
            <option value="10">10 Lots</option>
          </select>
          <select className="ema-select" value={scanInterval} onChange={e => setScanInterval(e.target.value)} disabled={scanning}>
            <option value="10">10s</option><option value="30">30s</option><option value="60">1m</option><option value="300">5m</option>
          </select>
          <button className={`btn ${scanning ? "btn-danger" : "btn-primary"}`} onClick={toggleScan} disabled={loading}>
            {loading ? "Scanning..." : scanning ? "Stop Scan" : "Start Scan"}
          </button>
          <select className="ema-select direction-select" value={tradeDirection} onChange={e => setTradeDirection(e.target.value)}>
            <option value="BOTH">Both</option><option value="LONG">Long Only</option><option value="SHORT">Short Only</option>
          </select>
        </div>
      </div>

      {/* Smart Filters Bar */}
      <div style={{display:"flex",gap:16,padding:"8px 12px",background:"var(--panel3)",borderBottom:"1px solid var(--surface3)",fontSize:12,alignItems:"center",flexWrap:"wrap"}}>
        <span style={{color:"#8b5cf6",fontWeight:700}}>SMART FILTERS:</span>
        <label style={{display:"flex",alignItems:"center",gap:4,color:"var(--grey-text)",cursor:"pointer"}}>
          <input type="checkbox" checked={useSmartFibs} onChange={e => setUseSmartFibs(e.target.checked)} />
          23.6% & 38.2% only
        </label>
        <label style={{display:"flex",alignItems:"center",gap:4,color:"var(--grey-text)",cursor:"pointer"}}>
          <input type="checkbox" checked={skipFirstHour} onChange={e => setSkipFirstHour(e.target.checked)} />
          Skip before 10:00
        </label>
        <label style={{display:"flex",alignItems:"center",gap:4,color:"var(--grey-text)",cursor:"pointer"}}>
          <input type="checkbox" checked={earlyExitEnabled} onChange={e => setEarlyExitEnabled(e.target.checked)} />
          Early exit (no 0.3R in 5 checks)
        </label>
        <label style={{display:"flex",alignItems:"center",gap:4,color:"var(--grey-text)",cursor:"pointer"}}>
          <input type="checkbox" checked={useVwap} onChange={e => setUseVwap(e.target.checked)} />
          VWAP
        </label>
        <label style={{display:"flex",alignItems:"center",gap:4,color:"var(--grey-text)",cursor:"pointer"}}>
          <input type="checkbox" checked={useSR} onChange={e => setUseSR(e.target.checked)} />
          S/R
        </label>
        <span style={{color:"var(--edge)"}}>|</span>
        <span style={{color:"var(--btn-border)"}}>Max/day:</span>
        <select className="ema-select" value={maxTradesPerDay} onChange={e => setMaxTradesPerDay(e.target.value)} style={{width:50}}>
          <option value="2">2</option><option value="3">3</option><option value="5">5</option><option value="10">10</option>
        </select>
      </div>

      {/* Auto-trade status bar */}
      {(scanning || autoTrading) && (
        <div className={`auto-status-bar ${autoTrading ? "auto-status-trading" : "auto-status-scanning"}`}>
          <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
            <span className="auto-status-item"><span className="pulse-dot"></span>
              {autoTrading ? (simulateMode ? "PAPER TRADING" : "LIVE TRADING") : `Auto-Scan ON (${parseInt(scanInterval) < 60 ? `${scanInterval}s` : `${Math.round(parseInt(scanInterval)/60)}m`})`}
            </span>
            {lastScanTime && <span className="auto-status-item">Last: {lastScanTime.toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit",second:"2-digit",hour12:false})}</span>}
            {data && <span className="auto-status-item">All signals: {data.totalSignals} | Filtered: {filteredSignals.length}</span>}
            {liveNiftyPrice && <span className="auto-status-item">{index}: <strong>{liveNiftyPrice.toFixed(1)}</strong></span>}
            {tradeError && <span className="auto-status-item" style={{color:"#f59e0b"}}>{tradeError}</span>}
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <label style={{display:"flex",alignItems:"center",gap:4,fontSize:12,color:"var(--grey-text)"}}>
              <input type="checkbox" checked={simulateMode} onChange={e => setSimulateMode(e.target.checked)} />
              Simulate
            </label>
            <button className={`btn btn-sm ${autoTrading ? "btn-danger" : "btn-success"}`} onClick={toggleAutoTrade}>
              {autoTrading ? "Stop Trading" : "Start Trading"}
            </button>
          </div>
        </div>
      )}

      {error && <div className="error-msg">{error}</div>}

      {/* View Toggle */}
      <div style={{display:"flex",gap:8,padding:"8px 12px",borderBottom:"1px solid var(--surface3)"}}>
        <button className={`btn btn-sm ${activeView === "scan" ? "btn-primary" : ""}`} onClick={() => setActiveView("scan")}>Live Scan</button>
        <button className={`btn btn-sm ${activeView === "backtest" ? "btn-primary" : ""}`} onClick={() => setActiveView("backtest")}>Backtest</button>
      </div>

      {/* ===== BACKTEST VIEW ===== */}
      {activeView === "backtest" && (
        <div className="bt-section">
          <div className="bt-header">
            <h3>Backtest (Smart Filtered)</h3>
            <div className="bt-controls">
              <select className="ema-select" value={btTimeframe} onChange={e => setBtTimeframe(e.target.value)}>
                <option value="5minute">5 Min</option><option value="15minute">15 Min</option>
                <option value="30minute">30 Min</option><option value="60minute">1 Hour</option><option value="day">Daily</option>
              </select>
              <select className="ema-select" value={btDateMode} onChange={e => setBtDateMode(e.target.value)}>
                <option value="days">By Days</option><option value="custom">Custom Dates</option>
              </select>
              {btDateMode === "days" ? (
                <select className="ema-select" value={btDays} onChange={e => setBtDays(e.target.value)}>
                  <option value="1">1 Day</option><option value="2">2 Days</option><option value="3">3 Days</option><option value="5">5 Days</option>
                  <option value="10">10 Days</option><option value="30">30 Days</option><option value="60">60 Days</option><option value="90">90 Days</option>
                </select>
              ) : (
                <>
                  <input type="date" className="ema-select date-input" value={btFromDate} onChange={e => setBtFromDate(e.target.value)} title="From date" />
                  <input type="date" className="ema-select date-input" value={btToDate} onChange={e => setBtToDate(e.target.value)} title="To date" />
                </>
              )}
              <select className="ema-select" value={btTarget} onChange={e => setBtTarget(e.target.value)}>
                <option value="1">Target 1:1</option><option value="1.5">Target 1:1.5</option>
                <option value="2">Target 1:2</option><option value="3">Target 1:3</option>
              </select>
              <select className="ema-select" value={btLots} onChange={e => setBtLots(e.target.value)}>
                <option value="1">1 Lot</option><option value="2">2 Lots</option><option value="3">3 Lots</option><option value="5">5 Lots</option>
                <option value="10">10 Lots</option><option value="15">15 Lots</option><option value="20">20 Lots</option><option value="25">25 Lots</option><option value="50">50 Lots</option>
              </select>
              <select className="ema-select" value={btStrike} onChange={e => setBtStrike(e.target.value)}>
                <option value="ATM">ATM</option><option value="ITM">ITM</option>
              </select>
              <label className="bt-trailing-label"><input type="checkbox" checked={btTrailing} onChange={e => setBtTrailing(e.target.checked)} /> Trailing SL</label>
              <label className="bt-trailing-label"><input type="checkbox" checked={btPartial} onChange={e => setBtPartial(e.target.checked)} /> Partial Exit</label>
              <button className="btn btn-secondary" onClick={runBacktest} disabled={btLoading}>{btLoading ? "Running..." : "Run Backtest"}</button>
            </div>
          </div>
          {btData && !btLoading && (
            <>
              <div style={{display:"flex",gap:16,padding:"8px 12px",background:"var(--panel3)",borderRadius:6,margin:"8px 0",fontSize:12,flexWrap:"wrap",alignItems:"center"}}>
                <span style={{color:"#8b5cf6",fontWeight:700}}>SMART:</span>
                <span>{btData.summary?.totalTrades} trades</span>
                <span className={btData.summary?.totalOptionPnl >= 0 ? "positive" : "negative"}>
                  {btData.summary?.totalOptionPnl >= 0 ? "+" : ""}₹{Math.abs(btData.summary?.totalOptionPnl || 0).toLocaleString("en-IN")}
                </span>
                <span style={{color:"var(--edge)"}}>|</span>
                <span style={{color:"var(--btn-border)"}}>UNFILTERED:</span>
                <span>{btData._unfilteredCount} trades</span>
                <span className={btData._unfilteredPnl >= 0 ? "positive" : "negative"}>
                  {btData._unfilteredPnl >= 0 ? "+" : ""}₹{Math.abs(btData._unfilteredPnl || 0).toLocaleString("en-IN")}
                </span>
              </div>
              <div className="bt-dir-filter">
                <span className="bt-dir-label">Filter:</span>
                {["ALL", "SHORT", "LONG"].map(dir => (
                  <button key={dir} className={`bt-dir-btn ${btDirFilter === dir ? "active" : ""} ${dir === "SHORT" ? "btn-short" : dir === "LONG" ? "btn-long" : ""}`}
                    onClick={() => setBtDirFilter(dir)}>{dir === "ALL" ? "All" : dir}</button>
                ))}
              </div>
            </>
          )}
          {btError && <div className="error-msg">{btError}</div>}
          {btLoading && <div className="loading"><div className="spinner" /><p>Running backtest...</p></div>}
          {btData && !btLoading && (() => {
            const s = btData.summary;
            const allTrades = btDirFilter === "ALL" ? btData.trades : btData.trades.filter(t => t.type === btDirFilter);
            const eq = []; let cum = 0, cumOpt = 0;
            allTrades.forEach(t => { cum += t.pnl||0; cumOpt += t.option?.moneyPnl||0; eq.push({date:t.exitDate, pnl:cum, optPnl:cumOpt, type:t.type}); });
            return (
              <div className="backtest-results">
                <h3>Backtest — {btData.symbol} ({btData.timeframe}, {btData.period})</h3>
                <p className="bt-candle-count">{btData.totalCandles} candles analyzed | Smart filtered: {s.totalTrades} of {btData._unfilteredCount} trades</p>

                <div className={`bt-money-banner ${s.totalOptionPnl >= 0 ? "money-profit" : "money-loss"}`}>
                  <div className="bt-money-amount">{s.totalOptionPnl >= 0 ? "+" : ""}₹{Math.abs(s.totalOptionPnl||0).toLocaleString("en-IN")}</div>
                  <div className="bt-money-detail">{s.lots} lots × {s.lotSize} qty = {s.qty} units | {s.strikeType} | {s.totalTrades} trades</div>
                </div>

                <div className="bt-summary-grid">
                  <div className={`bt-card ${s.totalPnl >= 0 ? "bt-card-green" : "bt-card-red"}`}><div className="bt-card-value">{s.totalPnl >= 0 ? "+" : ""}{s.totalPnl} pts</div><div className="bt-card-label">Index P&L</div></div>
                  <div className="bt-card"><div className="bt-card-value">{s.totalTrades}</div><div className="bt-card-label">Total Trades</div></div>
                  <div className={`bt-card ${s.winRate >= 50 ? "bt-card-green" : "bt-card-red"}`}><div className="bt-card-value">{s.winRate}%</div><div className="bt-card-label">Win Rate</div></div>
                  <div className={`bt-card ${s.profitFactor >= 1 ? "bt-card-green" : "bt-card-red"}`}><div className="bt-card-value">{s.profitFactor === Infinity ? "INF" : s.profitFactor}</div><div className="bt-card-label">Profit Factor</div></div>
                  <div className="bt-card bt-card-red"><div className="bt-card-value">{s.maxDrawdown} pts</div><div className="bt-card-label">Max Drawdown</div></div>
                  {s.capitalPerTrade > 0 && <div className="bt-card"><div className="bt-card-value">₹{s.capitalPerTrade.toLocaleString("en-IN")}</div><div className="bt-card-label">Capital/Trade</div></div>}
                  {s.roi !== undefined && s.roi !== 0 && <div className={`bt-card ${s.roi >= 0 ? "bt-card-green" : "bt-card-red"}`}><div className="bt-card-value">{s.roi >= 0 ? "+" : ""}{s.roi}%</div><div className="bt-card-label">ROI</div></div>}
                </div>

                <div className="bt-dir-pnl-cards">
                  <div className={`bt-dir-card bt-dir-card-short ${(btData.shortStats?.totalOptionPnl||0) >= 0 ? "bt-card-green" : "bt-card-red"}`}>
                    <div className="bt-dir-card-title">SHORT (Buy PUT)</div>
                    <div className="bt-dir-card-optpnl">{(btData.shortStats?.totalOptionPnl||0) >= 0 ? "+" : ""}₹{Math.abs(btData.shortStats?.totalOptionPnl||0).toLocaleString("en-IN")}</div>
                    <div className="bt-dir-card-meta">{btData.shortStats?.totalTrades||0} trades | {btData.shortStats?.winRate||0}% win</div>
                  </div>
                  <div className={`bt-dir-card bt-dir-card-long ${(btData.longStats?.totalOptionPnl||0) >= 0 ? "bt-card-green" : "bt-card-red"}`}>
                    <div className="bt-dir-card-title">LONG (Buy CALL)</div>
                    <div className="bt-dir-card-optpnl">{(btData.longStats?.totalOptionPnl||0) >= 0 ? "+" : ""}₹{Math.abs(btData.longStats?.totalOptionPnl||0).toLocaleString("en-IN")}</div>
                    <div className="bt-dir-card-meta">{btData.longStats?.totalTrades||0} trades | {btData.longStats?.winRate||0}% win</div>
                  </div>
                </div>

                {eq.length > 0 && (
                  <div className="bt-equity">
                    <h4>Equity Curve ({allTrades.length} trades)</h4>
                    <div className="bt-equity-chart">
                      {(() => {
                        const allVals = [...eq.map(p => p.pnl), ...eq.map(p => p.optPnl), 0];
                        const maxV = Math.max(...allVals), minV = Math.min(...allVals);
                        const range = maxV - minV || 1, h = 140, w = eq.length * 20 + 20;
                        const yOf = v => h - ((v - minV) / range) * h;
                        return (
                          <svg width="100%" height={h+30} viewBox={`0 0 ${w} ${h+30}`}>
                            <line x1="0" y1={yOf(0)} x2={w} y2={yOf(0)} stroke="var(--border-strong)" strokeDasharray="4" />
                            <polyline fill="none" stroke={eq[eq.length-1]?.pnl >= 0 ? "#69f0ae" : "#ff5252"} strokeWidth="2"
                              points={eq.map((p,i) => `${i*20+10},${yOf(p.pnl)}`).join(" ")} />
                            <polyline fill="none" stroke="#ffc107" strokeWidth="2" strokeDasharray="6,3"
                              points={eq.map((p,i) => `${i*20+10},${yOf(p.optPnl)}`).join(" ")} />
                            {eq.map((p,i) => (
                              <circle key={i} cx={i*20+10} cy={yOf(p.pnl)} r="3" fill={p.type==="SHORT"?"#ff5252":"#69f0ae"}>
                                <title>{`${p.type}: ${p.pnl>=0?"+":""}${p.pnl.toFixed(1)} pts`}</title>
                              </circle>
                            ))}
                          </svg>
                        );
                      })()}
                    </div>
                  </div>
                )}

                <div className="bt-trade-log">
                  <h4>Trade Log ({allTrades.length} trades)</h4>
                  <div className="stocks-table-wrapper">
                    <table className="stocks-table">
                      <thead><tr>
                        <th>Type</th><th>Fib</th><th>Signal</th><th>Exit Time</th><th>Entry</th><th>SL</th><th>Target</th><th>Exit</th>
                        <th>Reason</th><th>P&L</th><th>Option</th><th>Buy Prem</th><th>Sell Prem</th><th>Capital</th><th>₹ P&L (net)</th><th>R</th>
                      </tr></thead>
                      <tbody>
                        {allTrades.slice().reverse().map((t, i) => (
                          <tr key={i} className={t.result === "WIN" ? "trade-win" : t.result === "LOSS" ? "trade-loss" : ""}>
                            <td><span className={`signal-badge ${t.type === "SHORT" ? "badge-short" : "badge-long"}`}>{t.type}</span></td>
                            <td><span className="fib-badge">{t.fibLevel}</span></td>
                            <td style={{fontSize:11}}>{t.triggerDate ? new Date(t.triggerDate).toLocaleString("en-IN",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit",hour12:false}) : "-"}</td>
                            <td style={{fontSize:11}}>{t.exitDate ? new Date(t.exitDate).toLocaleString("en-IN",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit",hour12:false}) : "-"}</td>
                            <td>{t.entry?.toFixed(1)}</td>
                            <td className="sl">{t.sl?.toFixed(1)}</td>
                            <td>{t.target?.toFixed(1)}</td>
                            <td>{t.exitPrice?.toFixed(1)}</td>
                            <td><span className={`exit-reason reason-${(t.result||"").toLowerCase()}`}>{t.exitReason}</span></td>
                            <td className={t.pnl >= 0 ? "positive" : "negative"}>{t.pnl >= 0 ? "+" : ""}{t.pnl?.toFixed(1)}</td>
                            <td className="opt-cell">{t.option ? `${t.option.type} ${t.option.strike}` : "-"}
                              {t.option?.premiumSource === "ACTUAL" ? <span className="actual-badge">ACTUAL</span> : <span className="est-badge">EST</span>}
                            </td>
                            <td className="premium-cell">{t.option?.entryPremium ? `₹${t.option.entryPremium.toFixed(1)}` : "-"}</td>
                            <td className="premium-cell">{t.option?.exitPremium ? `₹${t.option.exitPremium.toFixed(1)}` : "-"}</td>
                            <td className="premium-cell">{t.option?.entryPremium ? `₹${Math.round(t.option.entryPremium * (t.option.qty||130)).toLocaleString("en-IN")}` : "-"}</td>
                            <td className={(t.option?.moneyPnl||0) >= 0 ? "positive" : "negative"}>
                              {t.option ? `${(t.option.moneyPnl||0) >= 0 ? "+" : ""}₹${Math.abs(t.option.moneyPnl||0).toLocaleString("en-IN")}` : "-"}
                            </td>
                            <td className={(t.rMultiple||0) >= 0 ? "positive" : "negative"}>{(t.rMultiple||0) >= 0 ? "+" : ""}{t.rMultiple?.toFixed(2)}R</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ===== SCAN VIEW ===== */}
      {activeView === "scan" && <>

      {/* Option Chain & Fib Info */}
      {data && (
        <div style={{padding:"8px 12px",display:"flex",gap:16,flexWrap:"wrap",fontSize:12,color:"var(--grey-text2)",borderBottom:"1px solid var(--surface2)"}}>
          {data.optionChain?.spotPrice && <span>Spot: <strong style={{color:"#fff"}}>{data.optionChain.spotPrice.toFixed(1)}</strong></span>}
          {data.optionChain?.atmStrike && <span>ATM Strike: <strong style={{color:"#fff"}}>{data.optionChain.atmStrike}</strong></span>}
          {data.optionChain?.nearestExpiry && <span>Expiry: <strong>{data.optionChain.nearestExpiry}</strong></span>}
          {data.fibData && <>
            <span>Swing H: <strong>{data.fibData.swingHigh?.price?.toFixed(1)}</strong></span>
            <span>Swing L: <strong>{data.fibData.swingLow?.price?.toFixed(1)}</strong></span>
            <span style={{color:"#8b5cf6"}}>Trend: <strong>{data.fibData.isUptrend ? "UP" : "DOWN"}</strong></span>
          </>}
          {data.vwapCurrent && <span>VWAP: <strong>{data.vwapCurrent}</strong></span>}
        </div>
      )}

      {/* Signals Table */}
      {filteredSignals.length > 0 && (
        <div style={{padding:"8px 12px"}}>
          <h4 style={{margin:"0 0 8px",fontSize:13,color:"var(--grey-text)"}}>Filtered Signals ({filteredSignals.length} of {data?.totalSignals || 0})</h4>
          <div className="stocks-table-wrapper">
            <table className="stocks-table">
              <thead><tr>
                <th>Time</th><th>Type</th><th>Fib</th><th>Entry</th><th>SL</th><th>Risk</th><th>Target</th><th>Option</th><th>Confluence</th><th>Detail</th>
              </tr></thead>
              <tbody>
                {filteredSignals.slice(-10).reverse().map((s, i) => {
                  const targetMultiple = s.recommendedTarget || FIB_TARGET_MAP[s.fibLevel] || parseFloat(scanTarget) || 2;
                  const target = s.type === "SHORT" ? s.entryPrice - s.risk * targetMultiple : s.entryPrice + s.risk * targetMultiple;
                  const atmStrike = data?.optionChain?.atmStrike;
                  const isPut = s.type === "SHORT";
                  const strikeData = atmStrike && data?.optionChain?.strikes?.find(st => st.strike === atmStrike);
                  const opt = isPut ? strikeData?.pe : strikeData?.ce;
                  return (
                    <tr key={i}>
                      <td style={{fontSize:11}}>{s.signalCandle?.date ? new Date(s.signalCandle.date).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit",hour12:false}) : "-"}</td>
                      <td><span className={`signal-badge ${s.type === "SHORT" ? "badge-short" : "badge-long"}`}>{s.type}</span></td>
                      <td><span className="fib-badge">{s.fibLevel}</span></td>
                      <td>{s.entryPrice?.toFixed(1)}</td>
                      <td className="sl">{s.stopLoss?.toFixed(1)}</td>
                      <td>{s.risk?.toFixed(1)}</td>
                      <td>{target?.toFixed(1)}</td>
                      <td className="opt-cell" style={{fontSize:11}}>
                        {opt ? `${isPut ? "PE" : "CE"} ${atmStrike}` : "-"}
                        {opt?.symbol && <div style={{fontSize:9,color:"var(--btn-border)"}}>{opt.symbol}</div>}
                      </td>
                      <td style={{fontSize:11}}>{(s.confluence || []).join(" + ") || "-"}</td>
                      <td style={{fontSize:10,maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",color:"var(--grey-text2)"}} title={s.detail}>{s.detail}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Scan data summary */}
      {data && !filteredSignals.length && (
        <div style={{padding:"12px",color:"var(--grey-text2)",fontSize:13}}>
          {data.totalSignals > 0
            ? `${data.totalSignals} signals found but all filtered out by smart filters.`
            : "No signals found. Waiting for next scan..."}
          {data.currentSetup && <div style={{marginTop:8,color:"#f59e0b"}}>{data.currentSetup.message}</div>}
        </div>
      )}

      </>}

      {/* Trade Log */}
      <div style={{padding:"8px 12px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <h3 style={{margin:0,fontSize:14,color:"var(--grey-text)"}}>Trade Log</h3>
          <div style={{display:"flex",gap:8}}>
            <button className="btn btn-export" onClick={() => exportLiveTradesCSV(tradeLog, `smart2-${index}-trades`)}>Export CSV</button>
            {tlSummary.closed > 0 && (
              <button className="btn btn-sm btn-danger" onClick={async () => {
                if (!window.confirm(`Delete all ${tlSummary.closed} closed trades?`)) return;
                await axios.post(`${API}/auto-trade/delete-trades`, { deleteAllClosed: true, index }).catch(() => {});
                setTradeLog(prev => prev.filter(t => t.status === "OPEN"));
              }}>Clear Closed ({tlSummary.closed})</button>
            )}
          </div>
        </div>

        <div style={{fontSize:12,color:"var(--grey-text2)",marginBottom:8,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
          <input type="date" className="ema-select date-input" value={tlFilterDate} onChange={e => setTlFilterDate(e.target.value)} title="From date" style={{minWidth:120}} />
          <span style={{color:"var(--muted2)",fontSize:11}}>→</span>
          <input type="date" className="ema-select date-input" value={tlFilterDateTo} onChange={e => setTlFilterDateTo(e.target.value)} title="To date (empty = open-ended)" style={{minWidth:120}} />
            {ERA_VIEWS.map(era => (
              <button key={era.name} onClick={() => { setTlFilterDate(era.from); setTlFilterDateTo(""); }} title={era.title}
                style={{
                  background: tlFilterDate === era.from && !tlFilterDateTo ? "color-mix(in srgb, var(--info) 20%, transparent)" : "var(--btn-muted)",
                  color: "var(--grey-light)", border: "1px solid var(--btn-border)", borderRadius: 12,
                  fontSize: 10.5, fontWeight: 600, padding: "3px 10px", cursor: "pointer",
                }}>{era.name}</button>
            ))}
          <select className="ema-select" value={tlFilterExpiry} onChange={e => setTlFilterExpiry(e.target.value)} title="Contract-expiry filter" style={{minWidth:100}}>
            <option value="ALL">All expiries</option><option value="EXPIRY">Expiry day</option><option value="NONEXP">Non-expiry</option>
          </select>
          {(tlFilterDate || tlFilterDateTo) && <button className="btn btn-sm btn-secondary" onClick={() => { setTlFilterDate(""); setTlFilterDateTo(""); }}>All Dates</button>}
          <span>
          {filteredTradeLog.length} trade{filteredTradeLog.length !== 1 ? "s" : ""}
          {tlSummary.open > 0 && <> | <span style={{color:"#ffa726"}}>{tlSummary.open} open</span></>}
          {tlSummary.closed > 0 && <> | {tlSummary.wins}W {tlSummary.closed - tlSummary.wins}L ({Math.round(tlSummary.wins / tlSummary.closed * 100)}%)</>}
          {tlSummary.totalPnl !== 0 && <> | <span className={tlSummary.totalPnl >= 0 ? "positive" : "negative"}>{tlSummary.totalPnl >= 0 ? "+" : "-"}₹{Math.abs(tlSummary.totalPnl).toLocaleString("en-IN")}</span></>}
              {tlSummary.closed > 0 && tradingDaysOf(filteredTradeLog.filter(t => t.status === "CLOSED")) > 0 && <> | avg/day <b className={tlSummary.totalPnl >= 0 ? "positive" : "negative"}>{(() => { const nd = tradingDaysOf(filteredTradeLog.filter(t => t.status === "CLOSED")); const v = tlSummary.totalPnl / nd; return `${v >= 0 ? "+" : "-"}₹${Math.abs(Math.round(v)).toLocaleString("en-IN")} (${nd}d)`; })()}</b></>}
          {(() => { const pc = peakConcurrentCapital(filteredTradeLog); return pc.peak > 0 && <> | peak capital <b title={pc.at ? `most capital deployed at once${pc.at ? " (at " + pc.at.toLocaleTimeString("en-IN", {hour:"2-digit",minute:"2-digit",hour12:false}) + ")" : ""}` : ""}>₹{pc.peak.toLocaleString("en-IN")}</b></>; })()}
          </span>
        </div>

        {filteredTradeLog.length > 0 && (<>
          <TradeCards trades={filteredTradeLog} livePremiums={livePremiums} onSelect={setDrawerTrade} onExit={exitTrade} />
          <div className="stocks-table-wrapper tl-table-wrap">
            <table className="stocks-table">
              <thead><tr>
                <th style={{width:40}}></th><th>Time</th><th>Exit</th><th>Index</th><th>Type</th><th>Fib</th><th>Option</th><th>Qty</th>
                <th>Entry</th><th>SL</th><th>Target</th>
                <th>Buy Prem</th><th>Sell Prem</th><th>Capital</th><th>P&L</th><th>Status</th>
              </tr></thead>
              <tbody>
                {filteredTradeLog.map(t => (
                  <tr key={t.id} className={t.status === "OPEN" ? "trade-open-row" : "trade-closed-row"}
                    style={{ cursor: "pointer" }} title="Click for trade details"
                    onClick={(e) => { if (e.target.closest("button,a")) return; setDrawerTrade(t); }}>
                    <td style={{whiteSpace:"nowrap"}}>
                      {t.status === "OPEN" && <button className="btn btn-sm btn-danger" onClick={() => exitTrade(t)} style={{marginRight:2,fontSize:10,padding:"2px 6px"}}>Exit</button>}
                      <button className="btn btn-sm" style={{background:"var(--btn-muted)",color:"var(--grey-light)",fontSize:10,padding:"2px 6px",border:"1px solid var(--btn-border)",borderRadius:4,cursor:"pointer"}}
                        onClick={async () => {
                          if (!window.confirm(`Delete trade ${t.tradingsymbol || t.type} @ ${t.entry}?`)) return;
                          await axios.post(`${API}/auto-trade/delete-trades`, { ids: [t.id] }).catch(() => {});
                          setTradeLog(prev => prev.filter(x => x.id !== t.id));
                        }}>Del</button>
                    </td>
                    <td>{new Date(t.time).toLocaleString("en-IN",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit",hour12:false})}</td><td>{t.exitTime ? new Date(t.exitTime).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "-"}</td>
                    <td style={{fontSize:10,color:"var(--grey-text)"}}>{t.index || "-"}</td>
                    <td><span className={`signal-badge ${t.type === "SHORT" ? "badge-short" : "badge-long"}`}>{t.type}</span></td>
                    <td><span className="fib-badge">{t.fibLevel || "-"}</span></td>
                    <td className="opt-cell">{t.signalType} {t.strike}{isExpiryDay(t) && <span title="Entered on its contract\u2019s expiry day (0DTE)" style={{ marginLeft: 4, fontSize: 9, fontWeight: 700, color: "var(--warn-text)", border: "1px solid var(--warn-text)", borderRadius: 3, padding: "0 3px" }}>EXP</span>}</td>
                    <td>{t.qty} ({t.lots}L){t.trailExitDone && (
                      <div className="trail-detail-label" style={{ fontSize: 10, marginTop: 2, fontWeight: 600 }}>
                        {(t.trailExitReason || "").includes("PROFIT") ? "Profit +20%" : "75% booked"}: {t.trailExitQty} @ ₹{t.trailExitPremium}
                        {t.trailExitPnl != null && <span className={t.trailExitPnl >= 0 ? "positive" : "negative"}> {t.trailExitPnl >= 0 ? "+" : ""}₹{t.trailExitPnl}</span>}
                        {t.remainingQty != null && <> · {t.remainingQty} trailing</>}
                      </div>
                    )}</td>
                    <td>{t.entry?.toFixed(1)}</td>
                    <td className="sl">{t.sl?.toFixed(1)}</td>
                    <td>{t.target?.toFixed(1)}</td>
                    <td className="premium-cell">
                      {t.entryPremium ? `₹${t.entryPremium.toFixed(1)}`
                        : t.status === "OPEN" && livePremiums[t.tradingsymbol]
                          ? <span className="live-premium">₹{livePremiums[t.tradingsymbol].toFixed(1)}</span>
                          : "-"}
                    </td>
                    <td className="premium-cell">
                      {t.status === "OPEN" && t.entryPremium && livePremiums[t.tradingsymbol]
                        ? <span className="live-premium">₹{livePremiums[t.tradingsymbol].toFixed(1)}</span>
                        : t.exitPremium ? `₹${t.exitPremium.toFixed(1)}` : "-"}
                    </td>
                    <td className="premium-cell">{t.capital ? `₹${t.capital.toLocaleString("en-IN")}` : "-"}</td>
                    <td className="premium-cell">
                      {(() => {
                        let pnl = null;
                        if (t.status === "OPEN" && livePremiums[t.tradingsymbol] && t.entryPremium) {
                          pnl = Math.round((livePremiums[t.tradingsymbol] - t.entryPremium) * (t.qty || 130));
                        } else if (t.netPnl != null) { pnl = t.netPnl; }
                        else if (t.grossPnl != null) { pnl = t.grossPnl; }
                        if (pnl == null) return "-";
                        return <span className={pnl >= 0 ? "positive" : "negative"}>{pnl >= 0 ? "+" : "-"}₹{Math.abs(pnl).toLocaleString("en-IN")}</span>;
                      })()}
                    </td>
                    <td style={{fontSize:10}}>{t.status}{t.exitReason ? ` (${t.exitReason})` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>)}
      </div>
      {drawerTrade && <TradeDrawer trade={drawerTrade} onClose={() => setDrawerTrade(null)} />}
    </div>
  );
}
